import { Location } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '@lohi/environments/environment';
import * as LogRocket from 'logrocket';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { AuthService } from './root/auth.service';

@Component({
  selector: 'lohi-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit, OnDestroy {
  public title$ = new BehaviorSubject<string | null>(null);
  public icon$ = new BehaviorSubject<'arrow_back_ios' | 'menu' | 'none' | null>('none');
  public showIcon$ = new BehaviorSubject(true);
  public showMainNav$$ = new BehaviorSubject(false);
  @Output() public toggle = new EventEmitter();

  private subscriptions: Subscription[] = [];
  public showLogo$$ = new BehaviorSubject(false);
  public showTitleBar$: Observable<boolean>;
  public userInfo$: any;
  public isLoggedIn$: Observable<boolean>;

  constructor(private router: Router, private location: Location, private authService: AuthService) {}

  public ngOnInit() {
    // if (!location.href.includes('pickup') && !location.href.includes('dropoff')) {
    //   this.showTitleBar$ = this.authService.isLoggedIn$;
    // }
    this.userInfo$ = this.authService.userInfo$;
    this.isLoggedIn$ = this.authService.isLoggedIn$;
    const lookItOverText = $localize`:@@lookitover:Look it over`;
    const confirmCoupleText = $localize`:@@confirmcouple:Confirm Couple`;
    const confirmUnCoupleText = $localize`:@@confirmuncouple:Confirm Uncouple`;
    const trailerNumberText = $localize`:@@trailerno:Trailer Number`;
    const submittingText = $localize`:@@submit:Submitting...`;
    const reportDamageText = $localize`:@@report-damage:Report Damage`;
    const inspectText = $localize`:@@inspect:Inspect`;
    this.enableLogRocket();

    this.subscriptions.push(
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
        this.showIcon$.next(true);
        this.showLogo$$.next(false);
        this.showMainNav$$.next(false);
        switch (event.url) {
          case '/pickup':
          case '/pickup/pickup':
          case '/pickup/write-in-detail':
          case '/pickup/write-in-plate-preview':
          case '/pickup/write-in-confirm-unhitch':
          case '/pickup-dropoff':
            this.title$.next(null);
            this.showIcon$.next(false);
            break;
          case '/pickup/choose-asset':
            this.title$.next(null);
            this.icon$.next('arrow_back_ios');
            break;
          case '/pickup/detail':
            this.title$.next(null);
            this.icon$.next('arrow_back_ios');
            break;
          case '/pickup/confirm-unhitch':
            this.title$.next(confirmCoupleText);
            this.icon$.next('arrow_back_ios');
            break;
          case '/pickup/plate-preview':
            this.title$.next(trailerNumberText);
            this.icon$.next('arrow_back_ios');
            break;
          case '/pickup/examine':
            this.title$.next(lookItOverText);
            this.icon$.next('arrow_back_ios');
            break;
          case '/pickup/submit':
            this.title$.next(submittingText);
            this.icon$.next(null);
            break;
          case '/pickup/report-damage':
            this.title$.next(reportDamageText);
            this.icon$.next('arrow_back_ios');
            break;
          case '/pickup/thank-you':
            this.showIcon$.next(false);
            this.title$.next(null);
            break;
          case '/dropoff':
          case '/dropoff/checkin':
            this.title$.next(null);
            this.showIcon$.next(false);
            break;
          case '/dropoff/dropoff':
            this.title$.next(null);
            this.icon$.next('arrow_back_ios');
            break;
          case '/dropoff/report-damage':
            this.title$.next(reportDamageText);
            this.icon$.next('arrow_back_ios');
            break;
          case '/dropoff/confirm-unhitch':
            this.title$.next(confirmUnCoupleText);
            this.icon$.next('arrow_back_ios');
            break;
          case '/dropoff/plate-preview':
            this.title$.next(trailerNumberText);
            this.icon$.next('arrow_back_ios');
            break;
          case '/dropoff/examine':
            this.title$.next(inspectText);
            this.icon$.next('arrow_back_ios');
            break;
          case '/dropoff/submit':
            this.title$.next(submittingText);
            this.icon$.next(null);
            break;
          case '/dropoff/thank-you':
            this.showIcon$.next(false);
            this.title$.next(null);
            break;
          default:
            this.showMainNav$$.next(true);
            this.showIcon$.next(true);
            this.title$.next(null);
            this.icon$.next('menu');
            this.showLogo$$.next(true);
            break;
        }
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public navigationAction() {
    this.icon$.pipe(take(1)).subscribe((icon) => {
      if (icon !== null) {
        if (icon === 'arrow_back_ios') {
          this.location.back();
        } else if (icon === 'menu') {
          this.toggle.next();
        }
      }
    });
  }

  public logout() {
    this.authService.logout();
  }

  private enableLogRocket() {
    if (window.location.hostname === 'localhost') {
      return;
    }
    const logRocketOptions = {};
    if (environment.production) {
      LogRocket.init('stkslu/lambo', logRocketOptions);
    } else {
      return;
    }
    // } else {
    //   LogRocket.init('stkslu/vorto-reload-stage', logRocketOptions);
    // }

    this.authService.isLoggedIn$
      .pipe(
        filter((t) => t),
        take(1),
      )
      .subscribe(async (isLoggedIn) => {
        if (isLoggedIn) {
          const user = await this.authService.userInfo$
            .pipe(
              filter((u) => !!u && !!u.id),
              take(1),
            )
            .toPromise();
          LogRocket.identify(user.id, {
            name: user.driverName,
            phone: user.phoneNumber,
          });
        }
      });
  }
}
